import { useState, useEffect, useMemo } from "react";
import {
  Grid,
  Typography,
  Chip,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Divider,
  FormControl,
  InputLabel,
  Select,
  Checkbox,
  FormControlLabel,
  Link,
} from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import SearchIcon from "@mui/icons-material/Search";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditProfileDialog from "../../../components/EditProfileDialog";
import AddPlayerDialog from "./AddPlayerDialog";
import request from "../../../utilities/requests";
import { toast } from "../../../utilities/SnackBarUtils";

const PlayerList = ({ eventId, leagueEvent, onLeagueEventUpdate }) => {
  const [attendees, setAttendees] = useState([]);
  const [pagination, setPagination] = useState({});
  const [loadingMore, setLoadingMore] = useState(false);

  const [menuOpen, setMenuOpen] = useState(false);
  const [anchor, setAnchor] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [editUserOpen, setEditUserOpen] = useState(false);
  const [removeUserOpen, setRemoveUserOpen] = useState(false);

  const [assignOpen, setAssignOpen] = useState(false);
  const [assignTeamId, setAssignTeamId] = useState("");
  const [assigning, setAssigning] = useState(false);

  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searching, setSearching] = useState(false);
  const [searchResults, setSearchResults] = useState([]);

  const [exportOptionsOpen, setExportOptionsOpen] = useState(false);
  const [includeProfileQuestions, setIncludeProfileQuestions] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [downloadLink, setDownloadLink] = useState(null);

  const [addPlayerOpen, setAddPlayerOpen] = useState(false);

  // Filter / sort
  const [teamFilter, setTeamFilter] = useState("all"); // "all" | "unassigned" | <teamId>
  const [sortBy, setSortBy] = useState("default"); // "default" | "name" | "team"

  const openExportOptions = () => {
    setIncludeProfileQuestions(false);
    setExportOptionsOpen(true);
  };

  const handleExportAttendees = async () => {
    if (!eventId) return;
    setExporting(true);
    try {
      const result = await request(
        `reports/eventattendees/?eventId=${eventId}&includeProfileQuestions=${includeProfileQuestions}`,
      );
      if (result?.success) {
        setExportOptionsOpen(false);
        setDownloadLink(result.url);
      } else {
        toast.error(result?.error || "Failed to export attendees");
      }
    } catch (e) {
      toast.error(e?.message || "Failed to export attendees");
    } finally {
      setExporting(false);
    }
  };

  // Build userId → team name map from leagueEvent
  const teamMap = {};
  if (leagueEvent?.teams) {
    for (const entry of leagueEvent.teams) {
      const teamName = entry.team?.name;
      for (const player of entry.roster || []) {
        const uid = player._id?.toString() || player.toString();
        teamMap[uid] = teamName;
      }
    }
  }

  const teams = leagueEvent?.teams || [];

  // Apply team filter + sort to loaded attendees.
  const displayedAttendees = useMemo(() => {
    let list = attendees;

    if (teamFilter === "unassigned") {
      list = list.filter((a) => !teamMap[a.userId?.toString()]);
    } else if (teamFilter !== "all") {
      // teamFilter is a teamId — find the matching team's roster
      const entry = teams.find(
        (t) => t.team?._id?.toString() === teamFilter,
      );
      const rosterIds = new Set(
        (entry?.roster || []).map((p) =>
          (p._id || p)?.toString(),
        ),
      );
      list = list.filter((a) => rosterIds.has(a.userId?.toString()));
    }

    if (sortBy === "name") {
      list = [...list].sort((a, b) => {
        const an = `${a.profile?.lastName || ""} ${a.profile?.firstName || ""}`
          .trim()
          .toLowerCase();
        const bn = `${b.profile?.lastName || ""} ${b.profile?.firstName || ""}`
          .trim()
          .toLowerCase();
        return an.localeCompare(bn);
      });
    } else if (sortBy === "team") {
      list = [...list].sort((a, b) => {
        const at = teamMap[a.userId?.toString()] || "￿"; // unassigned at end
        const bt = teamMap[b.userId?.toString()] || "￿";
        const cmp = at.toLowerCase().localeCompare(bt.toLowerCase());
        if (cmp !== 0) return cmp;
        // tie-break by name
        const an = `${a.profile?.lastName || ""} ${a.profile?.firstName || ""}`
          .trim()
          .toLowerCase();
        const bn = `${b.profile?.lastName || ""} ${b.profile?.firstName || ""}`
          .trim()
          .toLowerCase();
        return an.localeCompare(bn);
      });
    }

    return list;
  }, [attendees, teamFilter, sortBy, teams, teamMap]);

  // League rosters are bounded; load all attendees up front so filtering and
  // sorting see the entire set without depending on scroll.
  const getAttendees = async () => {
    if (loadingMore) return;
    setLoadingMore(true);
    const PAGE = 200; // server cap
    let offset = 0;
    let all = [];
    let lastPagination = null;
    try {
      while (true) {
        const result = await request(
          `leagueevent/attendees?eventId=${eventId}&limit=${PAGE}&offset=${offset}`,
        );
        if (!result?.success) break;
        all = all.concat(result.attendees || []);
        lastPagination = result.pagination;
        if (!result.attendees || result.attendees.length < PAGE) break;
        offset += PAGE;
      }
      setAttendees(all);
      setPagination(lastPagination || { limit: PAGE, offset: 0, total: all.length });
    } finally {
      setLoadingMore(false);
    }
  };

  const refreshLeagueEvent = async () => {
    const result = await request(`leagueevent?eventId=${eventId}`);
    if (result?.success && onLeagueEventUpdate) {
      onLeagueEventUpdate(result.data);
    }
  };

  useEffect(() => {
    if (eventId) {
      setSearching(false);
      getAttendees();
    }
  }, [eventId]);

  const runSearch = async () => {
    if (!searchQuery || searchQuery.trim().length < 2) {
      toast.error("Please enter at least 2 characters");
      return;
    }
    const result = await request(
      `leagueevent/attendees?eventId=${eventId}&search=${encodeURIComponent(searchQuery)}&limit=50`
    );
    if (result?.success) {
      setSearchResults(result.attendees);
      setSearching(true);
    } else {
      toast.error(result?.error || "Search failed");
    }
  };

  const handleSearchSelect = (attendee) => {
    setSelectedUser(attendee);
    setEditUserOpen(true);
    setSearchOpen(false);
    setSearchQuery("");
    setSearchResults([]);
    setSearching(false);
  };

  const handleMenuOpen = (event, item) => {
    setAnchor(event.currentTarget);
    setMenuOpen(true);
    setSelectedUser(item);
  };

  const handleDialogClose = () => {
    getAttendees();
    setEditUserOpen(false);
    setRemoveUserOpen(false);
    setSelectedUser(null);
    setMenuOpen(false);
  };

  const handleRemoveConfirm = async () => {
    const result = await request("events/removeattendee", "post", {
      userId: selectedUser?.userId || selectedUser?._id,
      eventId,
    });
    if (result?.success) {
      toast.success("Attendee removed");
      handleDialogClose();
    }
  };

  const openAssign = () => {
    // Pre-select the team the player is already on, if any
    const currentTeam = leagueEvent?.teams?.find((entry) =>
      (entry.roster || []).some(
        (p) => (p._id?.toString() || p.toString()) === selectedUser?.userId?.toString()
      )
    );
    setAssignTeamId(currentTeam?.team?._id?.toString() || "");
    setAssignOpen(true);
    setMenuOpen(false);
  };

  const handleAssign = async () => {
    if (!assignTeamId || !selectedUser) return;
    setAssigning(true);
    const result = await request("sportsteam/addplayer", "post", {
      leagueEventId: leagueEvent._id,
      teamId: assignTeamId,
      userId: selectedUser.userId,
    });
    setAssigning(false);
    if (result?.success) {
      toast.success("Player assigned to team");
      setAssignOpen(false);
      await refreshLeagueEvent();
    }
  };

  const renderTeamChip = (userId) => {
    const name = teamMap[userId?.toString()];
    if (!name) return <Typography variant="caption" color="text.secondary">—</Typography>;
    return <Chip label={name} size="small" variant="outlined" />;
  };

  const renderRows = (rows) =>
    rows.map((item) => (
      <Grid item key={item._id}>
        <Grid container direction="row" alignItems="center" style={{ minHeight: 48 }}>
          <Grid item xs={6} style={{ display: "flex", alignItems: "center" }}>
            <Typography>
              {item.profile?.firstName} {item.profile?.lastName}
            </Typography>
          </Grid>
          <Grid item xs={4} style={{ display: "flex", justifyContent: "flex-start" }}>
            {renderTeamChip(item.userId)}
          </Grid>
          <Grid item xs={2} style={{ display: "flex", justifyContent: "flex-end" }}>
            <MoreVertIcon
              onClick={(e) => handleMenuOpen(e, item)}
              style={{ cursor: "pointer" }}
            />
          </Grid>
        </Grid>
      </Grid>
    ));

  return (
    <>
      <Grid container direction="column" spacing={3}>
        {/* Header row with search */}
        <Grid item style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
          <Typography variant="subtitle2" sx={{ mr: "auto" }}>
            {teamFilter === "all"
              ? `${pagination?.total ?? attendees.length} players`
              : `${displayedAttendees.length} of ${pagination?.total ?? attendees.length} players`}
          </Typography>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Team</InputLabel>
            <Select
              value={teamFilter}
              label="Team"
              onChange={(e) => setTeamFilter(e.target.value)}
            >
              <MenuItem value="all">All teams</MenuItem>
              <MenuItem value="unassigned">Unassigned</MenuItem>
              {teams.map((entry) => (
                <MenuItem
                  key={entry.team?._id}
                  value={entry.team?._id?.toString()}
                >
                  {entry.team?.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Sort by</InputLabel>
            <Select
              value={sortBy}
              label="Sort by"
              onChange={(e) => setSortBy(e.target.value)}
            >
              <MenuItem value="default">Default</MenuItem>
              <MenuItem value="name">Name</MenuItem>
              <MenuItem value="team">Team</MenuItem>
            </Select>
          </FormControl>
          <Button
            variant="outlined"
            size="small"
            onClick={() => setAddPlayerOpen(true)}
          >
            Add Player
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={openExportOptions}
          >
            Export Attendees
          </Button>
          <IconButton onClick={() => setSearchOpen(true)} size="small">
            <SearchIcon />
          </IconButton>
        </Grid>

        {/* List */}
        <Grid item>
          {loadingMore ? (
            <Grid container justifyContent="center" style={{ padding: 24 }}>
              <CircularProgress size={28} />
            </Grid>
          ) : displayedAttendees.length > 0 ? (
            <Grid container direction="column">
              {/* Column headers */}
              <Grid item >
                <Grid container direction="row" alignItems="center">
                  <Grid item xs={6}>
                    <Typography variant="button">Name</Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="button">Team</Typography>
                  </Grid>
                  <Grid item xs={2} />
                </Grid>
              </Grid>

              {renderRows(displayedAttendees)}

              <Menu anchorEl={anchor} open={menuOpen} onClose={() => setMenuOpen(false)}>
                <MenuItem onClick={() => { setEditUserOpen(true); setMenuOpen(false); }}>Edit</MenuItem>
                <MenuItem onClick={openAssign}>Assign to Team</MenuItem>
                <Divider />
                <MenuItem onClick={() => { setRemoveUserOpen(true); setMenuOpen(false); }}>
                  Remove Attendee
                </MenuItem>
              </Menu>

              <EditProfileDialog
                open={editUserOpen}
                close={handleDialogClose}
                user={
                  selectedUser
                    ? { ...selectedUser, _id: selectedUser.userId || selectedUser._id }
                    : null
                }
                eventId={eventId}
              />
            </Grid>
          ) : attendees.length === 0 ? (
            <Typography variant="body2" color="text.secondary">No attendees found.</Typography>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No players match the current filter.
            </Typography>
          )}
        </Grid>
      </Grid>

      {/* Search dialog */}
      <Dialog
        open={searchOpen}
        onClose={() => { setSearchOpen(false); setSearchQuery(""); setSearchResults([]); setSearching(false); }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Search Players</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Search by name or email"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") runSearch(); }}
            margin="normal"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={runSearch} size="small">
                    <SearchIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          {searchResults.length > 0 && (
            <Grid container direction="column" spacing={1} style={{ marginTop: 8 }}>
              {searchResults.map((item) => (
                <Grid
                  item
                  key={item._id}
                  style={{ cursor: "pointer", padding: 8, borderRadius: 4 }}
                  onClick={() => handleSearchSelect(item)}
                >
                  <Grid container alignItems="center" spacing={1}>
                    <Grid item xs>
                      <Typography variant="body1">
                        {item.profile?.firstName} {item.profile?.lastName}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {item.email}
                      </Typography>
                    </Grid>
                    <Grid item>
                      {renderTeamChip(item.userId)}
                    </Grid>
                  </Grid>
                </Grid>
              ))}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setSearchOpen(false); setSearchQuery(""); setSearchResults([]); setSearching(false); }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Assign to team dialog */}
      <Dialog open={assignOpen} onClose={() => setAssignOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>
          Assign {selectedUser?.profile?.firstName} {selectedUser?.profile?.lastName} to Team
        </DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel>Team</InputLabel>
            <Select
              value={assignTeamId}
              label="Team"
              onChange={(e) => setAssignTeamId(e.target.value)}
            >
              {teams.map((entry) => (
                <MenuItem key={entry.team?._id} value={entry.team?._id?.toString()}>
                  {entry.team?.name}
                  {entry.division ? ` · ${entry.division}` : ""}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleAssign}
            disabled={!assignTeamId || assigning}
          >
            {assigning ? <CircularProgress size={20} /> : "Assign"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Remove confirm dialog */}
      <Dialog open={removeUserOpen} onClose={() => setRemoveUserOpen(false)}>
        <DialogTitle>Please confirm</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to remove{" "}
            <strong>
              {selectedUser?.profile?.firstName} {selectedUser?.profile?.lastName}
            </strong>{" "}
            from the event?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemoveUserOpen(false)}>Cancel</Button>
          <Button onClick={handleRemoveConfirm}>Confirm</Button>
        </DialogActions>
      </Dialog>

      {/* Export options dialog */}
      <Dialog
        open={exportOptionsOpen}
        onClose={() => (exporting ? null : setExportOptionsOpen(false))}
      >
        <DialogTitle>Export Attendees</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 1 }}>
            Choose what to include in the export.
          </DialogContentText>
          <FormControlLabel
            control={
              <Checkbox
                checked={includeProfileQuestions}
                onChange={(e) => setIncludeProfileQuestions(e.target.checked)}
                disabled={exporting}
              />
            }
            label="Include profile questions"
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setExportOptionsOpen(false)}
            disabled={exporting}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleExportAttendees}
            disabled={exporting}
            startIcon={exporting ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {exporting ? "Generating…" : "Download"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Download link dialog */}
      <Dialog
        open={downloadLink ? true : false}
        onClose={() => setDownloadLink(null)}
      >
        <DialogTitle>Click link to download file</DialogTitle>
        <DialogContent>
          <Link href={downloadLink}>Download</Link>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDownloadLink(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Add Player dialog */}
      <AddPlayerDialog
        open={addPlayerOpen}
        close={() => {
          setAddPlayerOpen(false);
          getAttendees();
        }}
        eventId={eventId}
      />

    </>
  );
};

export default PlayerList;
